import React from 'react';
import PropTypes from 'prop-types';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import {
  FormInput,
  CurrencyInput,
  FormSelect,
  SectionHeader,
  FormNavigation,
  FormRow,
  FormCheckbox,
} from '../FormComponents';

const liabilityTypeOptions = [
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'heloc', label: 'HELOC' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'student_loan', label: 'Student Loan' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'child_support', label: 'Child Support' },
  { value: 'alimony', label: 'Alimony' },
  { value: 'other', label: 'Other Debt' },
];

export default function Liabilities({ data, onChange, onNext, onBack }) {
  const liabilities = data.liabilities || [];

  const addLiability = () => {
    onChange({
      ...data,
      liabilities: [...liabilities, { id: Date.now(), type: '', creditor: '', balance: '', monthlyPayment: '', willPayOff: false }],
    });
  };

  const updateLiability = (id, field, value) => {
    onChange({
      ...data,
      liabilities: liabilities.map((liability) =>
        liability.id === id ? { ...liability, [field]: value } : liability
      ),
    });
  };

  const removeLiability = (id) => {
    onChange({
      ...data,
      liabilities: liabilities.filter((liability) => liability.id !== id),
    });
  };

  const totalMonthlyDebt = liabilities
    .filter(l => !l.willPayOff)
    .reduce((sum, l) => sum + (parseFloat(l.monthlyPayment) || 0), 0);

  const totalDebt = liabilities.reduce(
    (sum, l) => sum + (parseFloat(l.balance) || 0),
    0
  );

  // Allow skipping liabilities section
  const isValid = true;

  return (
    <div>
      <SectionHeader
        title="Liabilities"
        subtitle="List your current debts and monthly obligations"
        icon={<CreditCard size={28} />}
      />

      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        <div
          style={{
            flex: 1,
            padding: '16px 20px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05))',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <span style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Total Debt
          </span>
          <span style={{ fontSize: '20px', fontWeight: 700, color: '#f87171' }}>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
            }).format(totalDebt)}
          </span>
        </div>
        <div
          style={{
            flex: 1,
            padding: '16px 20px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.1), rgba(234, 88, 12, 0.05))',
            border: '1px solid rgba(251, 146, 60, 0.2)',
          }}
        >
          <span style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Monthly Payments
          </span>
          <span style={{ fontSize: '20px', fontWeight: 700, color: '#fb923c' }}>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
            }).format(totalMonthlyDebt)}
          </span>
        </div>
      </div>

      {liabilities.length === 0 && (
        <div
          style={{
            padding: '32px',
            borderRadius: '12px',
            border: '2px dashed rgba(148, 163, 184, 0.3)',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          <p style={{ color: '#94a3b8', margin: '0 0 8px' }}>
            No liabilities added.
          </p>
          <p style={{ color: '#64748b', margin: 0, fontSize: '13px' }}>
            If you have no debts, you can continue to the next section.
          </p>
        </div>
      )}

      {liabilities.map((liability, index) => (
        <div
          key={liability.id}
          style={{
            padding: '20px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#64748b',
              }}
            >
              Liability #{index + 1}
            </span>
            <button
              type="button"
              onClick={() => removeLiability(liability.id)}
              style={{
                background: 'none',
                border: 'none',
                color: '#f87171',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
              }}
            >
              <Trash2 size={14} /> Remove
            </button>
          </div>

          <FormRow>
            <FormSelect
              label="Liability Type"
              value={liability.type}
              onChange={(value) => updateLiability(liability.id, 'type', value)}
              options={liabilityTypeOptions}
              placeholder="Select type"
              required
            />
            <FormInput
              label="Creditor Name"
              value={liability.creditor}
              onChange={(value) => updateLiability(liability.id, 'creditor', value)}
              placeholder="Chase Bank"
            />
          </FormRow>

          <FormRow>
            <CurrencyInput
              label="Outstanding Balance"
              value={liability.balance}
              onChange={(value) => updateLiability(liability.id, 'balance', value)}
              required
              placeholder="$0"
            />
            <CurrencyInput
              label="Monthly Payment"
              value={liability.monthlyPayment}
              onChange={(value) => updateLiability(liability.id, 'monthlyPayment', value)}
              required
              placeholder="$0"
            />
          </FormRow>

          <FormCheckbox
            label="Will be paid off at or before closing"
            checked={liability.willPayOff || false}
            onChange={(value) => updateLiability(liability.id, 'willPayOff', value)}
            description="Check this if you plan to pay off this debt"
          />
        </div>
      ))}

      <button
        type="button"
        onClick={addLiability}
        style={{
          width: '100%',
          padding: '14px',
          borderRadius: '12px',
          border: '2px dashed rgba(239, 68, 68, 0.4)',
          background: 'rgba(239, 68, 68, 0.05)',
          color: '#f87171',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          marginBottom: '8px',
        }}
      >
        <Plus size={18} /> Add Liability
      </button>

      <p style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', marginBottom: '24px' }}>
        Include all recurring debts and obligations.
      </p>

      <FormNavigation
        onBack={onBack}
        onNext={onNext}
        onSkip={liabilities.length === 0 ? onNext : undefined}
        isValid={isValid}
      />
    </div>
  );
}

Liabilities.propTypes = {
  data: PropTypes.shape({
    liabilities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        creditor: PropTypes.string,
        balance: PropTypes.string,
        monthlyPayment: PropTypes.string,
        willPayOff: PropTypes.bool,
      })
    ),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
